import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Stack, Typography, useTheme } from "@mui/material";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import { toast } from "react-toastify";
import {
  useUpdateBugDetailMutation,
  useLazyGetBugDetailByBugIdQuery,
} from "@/features/bughurd/api";
import { useGetAttachmentUrlMutation } from "@/features/Global/api";
import axios from "axios";
import {
  loadWidgetFeedback,
  revokeWidgetFeedbackUrls,
  type WidgetFeedbackContent,
  type WidgetUploadKeys,
} from "@/utils/widgetFeedbackLoader";
import WidgetContentViewer from "./WidgetContentViewer";
import { fetchBaseQueryWithEncryption } from "@/features/fetchBaseQueryWithEncryption";
import RequestFeedbackDialog from "./RequestFeedbackDialog";
import RecordingLinkCard from "./RecordingLinkCard";
import ContentLoadingPlaceholder from "./ContentLoadingPlaceholder";

interface CustomerPanelProps {
  bugId: string;
  bugDetailData: any;
}

const POLL_INTERVAL = 30000; // 30 seconds

type ExpirationValue = "24h" | "3d" | "7d";
type DeliveryMethod = "copy" | "email";
type FlowType = "VIDEO" | "WIDGET";

const generateRecordingLink = async (payload: Record<string, any>) => {
  const response = await fetchBaseQueryWithEncryption(
    `${import.meta.env.VITE_COMPANY_API_URL}/file/shareable-link/generate`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
    },
  );
  console.log("response", response);
  return response.data.decryptedData;
  // if (!response.ok) {
  //   throw new Error("Failed to generate link");
  // }
  // const raw = await response.json();
  // const decryptedData = await cryptoDecryptData(raw?.data?.encryptData);
  // return decryptedData.data;
};

const CustomerPanel = ({ bugId, bugDetailData }: CustomerPanelProps) => {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();
  const [generatedUrl, setGeneratedUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isLoadingVideo, setIsLoadingVideo] = useState(false);
  const [updateBugDetail] = useUpdateBugDetailMutation();
  const [postAttachmentKey] = useGetAttachmentUrlMutation();
  const [fetchBugDetail] = useLazyGetBugDetailByBugIdQuery();
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [expiration, setExpiration] = useState<ExpirationValue>("3d");
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>("copy");
  const [includeAudio, setIncludeAudio] = useState(false);
  const [customerEmail, setCustomerEmail] = useState("");
  const [messageToCustomer, setMessageToCustomer] = useState(
    "Help us understand your issue better by recording your screen.",
  );
  const [flowType, setFlowType] = useState<FlowType>("VIDEO");
  const [customerUrl, setCustomerUrl] = useState("");
  const [feedbackContent, setFeedbackContent] =
    useState<WidgetFeedbackContent | null>(null);
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [pollCountdown, setPollCountdown] = useState(POLL_INTERVAL / 1000);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const customerData = useMemo(() => {
    if (!bugDetailData?.customerData) return null;
    try {
      return typeof bugDetailData.customerData === "string"
        ? JSON.parse(bugDetailData.customerData)
        : bugDetailData.customerData;
    } catch {
      return null;
    }
  }, [bugDetailData?.customerData]);

  useEffect(() => {
    if (customerData?.type === "WIDGET" && customerData?.widgetUrl) {
      setGeneratedUrl(customerData.widgetUrl);
      if (customerData.customerUrl) setCustomerUrl(customerData.customerUrl);
    } else if (customerData?.token) {
      setGeneratedUrl(
        `${import.meta.env.VITE_SNIFFER_WEB_URL}/record/${customerData.token}`,
      );
    }
  }, [
    customerData?.token,
    customerData?.type,
    customerData?.widgetUrl,
    customerData?.customerUrl,
  ]);

  const createdAt = useMemo(() => {
    if (!customerData?.createdAt) return null;
    return new Date(customerData.createdAt);
  }, [customerData?.createdAt]);

  const isExpired = useMemo(() => {
    if (!createdAt || !customerData?.expiresIn) return false;
    const expiryTime = createdAt.getTime() + customerData.expiresIn * 1000;
    return Date.now() > expiryTime;
  }, [createdAt, customerData?.expiresIn]);

  const resolveVideoUrl = useCallback(
    (key: string) => {
      setIsLoadingVideo(true);
      postAttachmentKey({ key })
        .then(async (res: any) => {
          const presignedUrl = res?.data?.data?.decryptedData?.url;
          if (!presignedUrl) return;
          const videoRes = await fetch(presignedUrl);
          if (!videoRes.ok) throw new Error("Failed to fetch video");
          const blob = await videoRes.blob();
          const blobUrl = URL.createObjectURL(blob);
          setVideoUrl(blobUrl);
        })
        .catch(() => {})
        .finally(() => {
          setIsLoadingVideo(false);
        });
    },
    [postAttachmentKey],
  );

  useEffect(() => {
    return () => {
      if (videoUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(videoUrl);
      }
      revokeWidgetFeedbackUrls(feedbackContent);
    };
  }, [videoUrl, feedbackContent]);

  const loadFeedbackFromS3 = useCallback(
    async (uploads: WidgetUploadKeys) => {
      setIsLoadingFeedback(true);
      setFeedbackError(null);
      try {
        const resolveKey = async (key: string): Promise<string | null> => {
          const res: any = await postAttachmentKey({ key });
          return res?.data?.data?.decryptedData?.url || null;
        };
        const content = await loadWidgetFeedback(uploads, resolveKey);
        setFeedbackContent(content);
      } catch {
        setFeedbackError("Failed to load customer feedback data");
      } finally {
        setIsLoadingFeedback(false);
      }
    },
    [postAttachmentKey],
  );

  useEffect(() => {
    if (customerData?.status !== "completed") return;

    if (customerData?.type === "WIDGET" && customerData?.uploads) {
      const uploads = customerData.uploads as Record<
        string,
        { fileKey?: string }
      >;
      const keys: WidgetUploadKeys = {};
      if (uploads.metadata?.fileKey) keys.metadata = uploads.metadata.fileKey;

      // Load images if uploaded
      if (uploads.screenshot?.fileKey)
        keys.screenshot = uploads.screenshot.fileKey;
      if (uploads.fullpage?.fileKey) keys.fullpage = uploads.fullpage.fileKey;
      if (uploads.recording?.fileKey) {
        keys.recording = uploads.recording.fileKey;
        if (uploads.console?.fileKey) keys.console = uploads.console.fileKey;
        if (uploads.network?.fileKey) keys.network = uploads.network.fileKey;
        if (uploads.actions?.fileKey) keys.actions = uploads.actions.fileKey;
      }
      loadFeedbackFromS3(keys);
    } else if (customerData?.uploadKey) {
      resolveVideoUrl(customerData.uploadKey);
    }
  }, [
    customerData?.status,
    customerData?.uploadKey,
    customerData?.uploads,
    customerData?.type,
    resolveVideoUrl,
    loadFeedbackFromS3,
  ]);

  // Poll for status changes while pending
  useEffect(() => {
    const shouldPoll =
      customerData?.status === "pending" &&
      !isExpired &&
      (customerData?.token || customerData?.type === "WIDGET");

    if (shouldPoll) {
      setPollCountdown(POLL_INTERVAL / 1000);

      countdownRef.current = setInterval(() => {
        setPollCountdown((prev) =>
          prev <= 1 ? POLL_INTERVAL / 1000 : prev - 1,
        );
      }, 1000);

      pollRef.current = setInterval(async () => {
        try {
          setIsRefreshing(true);
          await fetchBugDetail({ bugId, withDetails: true });
        } catch {
        } finally {
          setIsRefreshing(false);
          setPollCountdown(POLL_INTERVAL / 1000);
        }
      }, POLL_INTERVAL);
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, [
    customerData?.token,
    customerData?.type,
    customerData?.status,
    isExpired,
    bugId,
    fetchBugDetail,
  ]);

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    try {
      await fetchBugDetail({ bugId, withDetails: true });
    } catch {
    } finally {
      setIsRefreshing(false);
      setPollCountdown(POLL_INTERVAL / 1000);
    }
  };

  const buildWidgetUrl = (baseUrl: string, token: string): string => {
    try {
      const url = new URL(
        baseUrl.startsWith("http") ? baseUrl : `https://${baseUrl}`,
      );
      url.searchParams.set("sniffer", "true");
      url.searchParams.set("token", token);
      return url.toString();
    } catch {
      return baseUrl;
    }
  };

  const openRequestModal = () => {
    if (
      customerData?.expiration === "24h" ||
      customerData?.expiration === "3d" ||
      customerData?.expiration === "7d" ||
      customerData?.expiration === "30d"
    ) {
      setExpiration(customerData.expiration);
    }

    if (
      customerData?.deliveryMethod === "copy" ||
      customerData?.deliveryMethod === "email"
    ) {
      setDeliveryMethod(customerData.deliveryMethod);
    }
    if (typeof customerData?.messageToCustomer === "string") {
      setMessageToCustomer(customerData.messageToCustomer);
    }
    if (typeof customerData?.includeAudio === "boolean") {
      setIncludeAudio(customerData.includeAudio);
    }
    if (typeof customerData?.customerEmail === "string") {
      setCustomerEmail(customerData.customerEmail);
    }
    if (customerData?.type === "WIDGET" || customerData?.type === "VIDEO") {
      setFlowType(customerData.type);
    }
    if (typeof customerData?.customerUrl === "string") {
      setCustomerUrl(customerData.customerUrl);
    } else if (bugDetailData?.project?.domain) {
      setCustomerUrl(bugDetailData.project.domain);
    }
    setIsRequestModalOpen(true);
  };

  const handleGenerate = async () => {
    setIsLoading(true);
    try {
      const payload = {
        bugId,
        expiration,
        type: "VIDEO",
        message: messageToCustomer,
        withAudio: includeAudio,
      };

      const result = await generateRecordingLink(payload);
      const { token, expiresIn } = result;

      const s3Res = await axios.post(
        `${import.meta.env.VITE_COMPANY_API_URL}/file/s3/generate-upload-url`,
        {
          fileName: `customer_recording.webm`,
          uploadFor: "RECORDING",
          fileSize: 100 * 1024 * 1024,
          chunkUpload: true,
        },
        { withCredentials: true },
      );

      const uploadKey =
        s3Res?.data?.fileKey || s3Res?.data?.data?.localData?.key;
      const uploadId =
        s3Res?.data?.uploadId || s3Res?.data?.data?.localData?.uploadId;
      const parts = s3Res?.data?.parts || s3Res?.data?.data?.localData?.parts;

      await updateBugDetail({
        bugId,
        customerData: JSON.stringify({
          token,
          expiresIn,
          status: "pending",
          createdAt: new Date().toISOString(),
          uploadKey,
          uploadId,
          parts,
          type: "VIDEO",
          expiration,
          messageToCustomer,
          deliveryMethod,
          includeAudio,
          customerEmail: deliveryMethod === "email" ? customerEmail : "",
        }),
      }).unwrap();

      const recordingUrl = `${import.meta.env.VITE_SNIFFER_WEB_URL}/record/${token}`;
      setGeneratedUrl(recordingUrl);
      setVideoUrl(null);

      if (deliveryMethod === "copy") {
        await navigator.clipboard.writeText(recordingUrl);
      } else {
        // const to = encodeURIComponent(customerEmail);
        // const subject = encodeURIComponent(`Recording request for task ${bugId}`);
        // const body = encodeURIComponent(
        //   `${messageToCustomer}\n\n${recordingUrl}`,
        // );
        // window.open(`mailto:${to}?subject=${subject}&body=${body}`, "_blank");

        const response = await fetchBaseQueryWithEncryption(
          `${import.meta.env.VITE_COMPANY_API_URL}/sendRecordLink`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: {
              receiverEmail: customerEmail,
              supportMessage: messageToCustomer,
              recordLink: recordingUrl,
            },
          },
        );
        if (response.status === 200) {
          toast.success(t("Recording link sent to customer"));
        } else {
          toast.error(t("Failed to send recording link to customer"));
        }
      }

      await fetchBugDetail({ bugId, withDetails: true });
      setIsRequestModalOpen(false);
      toast.success(t("Recording link generated"));
    } catch {
      toast.error(t("Failed to generate recording link"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateWidget = async () => {
    setIsLoading(true);
    try {
      const result = await generateRecordingLink({
        bugId,
        expiration,
        type: "WIDGET",
        message: messageToCustomer,
        withAudio: includeAudio,
      });
      const attachmentTypes = [
        {
          key: "screenshot",
          fileName: `widget_screenshot_${bugId}.png`,
          size: 10 * 1024 * 1024,
        },
        {
          key: "fullpage",
          fileName: `widget_fullpage_${bugId}.png`,
          size: 20 * 1024 * 1024,
        },
        {
          key: "recording",
          fileName: `widget_recording_${bugId}.webm`,
          size: 100 * 1024 * 1024,
        },
        {
          key: "console",
          fileName: `widget_console_${bugId}.json`,
          size: 5 * 1024 * 1024,
        },
        {
          key: "network",
          fileName: `widget_network_${bugId}.json`,
          size: 5 * 1024 * 1024,
        },
        {
          key: "actions",
          fileName: `widget_actions_${bugId}.json`,
          size: 5 * 1024 * 1024,
        },
        {
          key: "metadata",
          fileName: `widget_metadata_${bugId}.json`,
          size: 1 * 1024 * 1024,
        },
      ];

      // Generate all S3 upload URLs in parallel
      const results = await Promise.all(
        attachmentTypes.map(({ fileName, size }) =>
          axios.post(
            `${import.meta.env.VITE_COMPANY_API_URL}/file/s3/generate-upload-url`,
            {
              fileName,
              uploadFor: "RECORDING",
              fileSize: size,
              chunkUpload: true,
            },
            { withCredentials: true },
          ),
        ),
      );

      // Build uploads map
      const uploads: Record<
        string,
        { fileKey: string; uploadId: string; parts: any[] }
      > = {};
      attachmentTypes.forEach(({ key }, i) => {
        const d = results[i]?.data;
        const local = d?.data?.localData;
        uploads[key] = {
          fileKey: d?.fileKey || local?.key,
          uploadId: d?.uploadId || local?.uploadId,
          parts: d?.parts || local?.parts,
        };
      });

      const builtWidgetUrl = buildWidgetUrl(customerUrl, result.token);

      await updateBugDetail({
        bugId,
        customerData: JSON.stringify({
          type: "WIDGET",
          status: "pending",
          createdAt: new Date().toISOString(),
          uploads,
          widgetUrl: builtWidgetUrl,
          customerUrl,
          expiration,
          messageToCustomer,
          deliveryMethod,
          customerEmail: deliveryMethod === "email" ? customerEmail : "",
        }),
      }).unwrap();

      setGeneratedUrl(builtWidgetUrl);
      setVideoUrl(null);
      setFeedbackContent(null);
      setFeedbackError(null);

      if (deliveryMethod === "copy") {
        await navigator.clipboard.writeText(builtWidgetUrl);
      } else {
        const to = encodeURIComponent(customerEmail);
        const subject = encodeURIComponent(
          `Feedback request for task ${bugId}`,
        );
        const body = encodeURIComponent(
          `${messageToCustomer}\n\n${builtWidgetUrl}`,
        );
        window.open(`mailto:${to}?subject=${subject}&body=${body}`, "_blank");
      }

      await fetchBugDetail({ bugId, withDetails: true });
      setIsRequestModalOpen(false);
      toast.success(t("Widget link generated"));
    } catch {
      toast.error(t("Failed to generate widget link"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(generatedUrl);
      toast.success(t("Link copied to clipboard!"));
    } catch {
      toast.error(t("Failed to generate recording link"));
    }
  };

  const statusColor =
    customerData?.status === "completed"
      ? "success"
      : isExpired
        ? "error"
        : "warning";

  const statusLabel = isExpired
    ? t("Expired")
    : customerData?.status === "completed"
      ? t("Completed")
      : t("Pending");

  const expirationLabel =
    customerData?.expiration === "24h"
      ? t("24 hours")
      : customerData?.expiration === "3d"
        ? t("3 days")
        : customerData?.expiration === "30d"
          ? t("30 days")
          : t("7 days");

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
      <RequestFeedbackDialog
        open={isRequestModalOpen}
        onClose={() => setIsRequestModalOpen(false)}
        isLoading={isLoading}
        bugKey={bugDetailData?.bugKey}
        flowType={flowType}
        setFlowType={setFlowType}
        includeAudio={includeAudio}
        setIncludeAudio={setIncludeAudio}
        expiration={expiration}
        setExpiration={setExpiration}
        messageToCustomer={messageToCustomer}
        setMessageToCustomer={setMessageToCustomer}
        deliveryMethod={deliveryMethod}
        setDeliveryMethod={setDeliveryMethod}
        customerEmail={customerEmail}
        setCustomerEmail={setCustomerEmail}
        customerUrl={customerUrl}
        projectDomain={bugDetailData?.project?.domain}
        onGenerate={handleGenerate}
        onGenerateWidget={handleGenerateWidget}
      />

      <RecordingLinkCard
        customerData={customerData}
        generatedUrl={generatedUrl}
        createdAt={createdAt}
        isExpired={isExpired}
        isLoading={isLoading}
        expirationLabel={expirationLabel}
        statusLabel={statusLabel}
        statusColor={statusColor}
        isRefreshing={isRefreshing}
        onCopyLink={handleCopyLink}
        onOpenRequestModal={openRequestModal}
        onManualRefresh={handleManualRefresh}
      />

      {/* Video Preview Section (VIDEO flow) */}
      {customerData?.status === "completed" &&
        customerData?.type !== "WIDGET" && (
          <Box
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              p: 3,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 1 }}
            >
              <VideocamOutlinedIcon color="primary" fontSize="small" />
              <Typography variant="subtitle1" fontWeight="bold">
                {t("Customer Recording")}
              </Typography>
            </Stack>

            {isLoadingVideo ? (
              <ContentLoadingPlaceholder
                icon={
                  <VideocamOutlinedIcon
                    sx={{ fontSize: 24, color: theme.palette.primary.main }}
                  />
                }
                title={t("Loading recording...")}
                description={t(
                  "Fetching the customer's screen recording from storage",
                )}
              />
            ) : videoUrl ? (
              <Box
                sx={{
                  borderRadius: 1,
                  overflow: "hidden",
                  backgroundColor: theme.palette.common.black,
                }}
              >
                <video
                  src={videoUrl}
                  controls
                  style={{ width: "100%", maxHeight: 400, display: "block" }}
                />
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t("Failed to load recording")}
              </Typography>
            )}
          </Box>
        )}

      {/* Customer Feedback Section (WIDGET flow) */}
      {customerData?.status === "completed" &&
        customerData?.type === "WIDGET" && (
          <Box
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              p: 3,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 1 }}
            >
              <FeedbackOutlinedIcon color="primary" fontSize="small" />
              <Typography variant="subtitle1" fontWeight="bold">
                {t("Customer Feedback")}
              </Typography>
            </Stack>

            {isLoadingFeedback ? (
              <ContentLoadingPlaceholder
                icon={
                  <FeedbackOutlinedIcon
                    sx={{ fontSize: 24, color: theme.palette.primary.main }}
                  />
                }
                title={t("Loading feedback data...")}
                description={t(
                  "Retrieving screenshots, recordings, and logs from the customer",
                )}
              />
            ) : feedbackError ? (
              <Alert severity="error">{t(feedbackError)}</Alert>
            ) : feedbackContent ? (
              <WidgetContentViewer feedbackContent={feedbackContent} />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t("No feedback data available")}
              </Typography>
            )}
          </Box>
        )}
    </Box>
  );
};

export default CustomerPanel;
